/**
 * Who counts as a "professional" account.
 *
 * Owner's definition (plan "Professional means", blueprint D7): a broker,
 * agent or company admin role, an accountType of broker / company / developer,
 * an active company team member, or a declared or live professional activity.
 * An investor alone is NOT a professional. Admins pass too, so they can see and
 * moderate the professional-only surfaces.
 *
 * MIRRORS the backend: professionalFromSession() and the ADMIN_ROLES / PRO_*
 * lists in backend/server/services/entitlement.service.js, used by
 * resolveProfessional() and middleware/professional.middleware.js. Keep both
 * sides identical. The backend is the side that enforces (the
 * /api/advertisements router, GET /api/entitlements); this helper only decides
 * what the UI can show without a round trip.
 *
 * It reads only what the session already carries: role, accountType,
 * declaredRoles and, for active team members, companyBadge (see
 * backend/server/storage/userStore.js toPublicUser and GET /auth/session).
 * Two signals live only on the server: an agent's companyId and live
 * AccountActivity rows. So "false" here means "not provably professional from
 * the session", and useEntitlements() asks the server for the full answer
 * (`professional.value`).
 */

/** User.role values that are professional on their own. */
export const PRO_ROLES = ['BROKER', 'AGENT', 'COMPANY_ADMIN'] as const;

/** Staff roles, allowed through every professional gate. */
export const ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN'] as const;

/** User.metadata.accountType values that are professional. */
export const PRO_ACCOUNT_TYPES = ['broker', 'company', 'developer'] as const;

/**
 * ActivityType values that are professional, for declaredRoles (signup) and
 * AccountActivity rows (server). INVESTOR is deliberately absent.
 */
export const PRO_ACTIVITY_TYPES = [
  'BROKER',
  'DEVELOPER',
  'PROPERTY_MANAGEMENT',
  'FACILITIES_MANAGEMENT',
  'APPRAISER',
  'CONSULTANT',
  'ANALYST',
] as const;

/**
 * Which session signal made the user professional. Same names as the backend's
 * `professional.via`, which can also be 'company' (an agent attached to a
 * company) or 'activity' (a live AccountActivity): server-only signals.
 */
export type ProfessionalVia = 'admin' | 'role' | 'accountType' | 'declaredRole' | 'team';

/**
 * The session fields this helper reads. `AuthUser` from hooks/useAuth satisfies
 * it; everything is `unknown` because the session is parsed JSON.
 */
export interface ProfessionalSignals {
  role?: unknown;
  accountType?: unknown;
  declaredRoles?: unknown;
  companyBadge?: unknown;
  metadata?: unknown;
}

const PRO_ROLE_SET: ReadonlySet<string> = new Set(PRO_ROLES);
const ADMIN_ROLE_SET: ReadonlySet<string> = new Set(ADMIN_ROLES);
const PRO_ACCOUNT_TYPE_SET: ReadonlySet<string> = new Set(PRO_ACCOUNT_TYPES);
const PRO_ACTIVITY_SET: ReadonlySet<string> = new Set(PRO_ACTIVITY_TYPES);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** User.metadata arrives as an object or as the raw JSON string from the database. */
function parseMetadata(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value)) ?? {};
    } catch {
      return {};
    }
  }
  return asRecord(value) ?? {};
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The signal that makes `user` professional, or null when the session alone
 * does not show it. Same checks, order and precedence as the backend's
 * professionalFromSession(): role, accountType (metadata first), declared roles
 * (arrays only, metadata first), then team membership.
 */
export function professionalVia(user: ProfessionalSignals | null | undefined): ProfessionalVia | null {
  if (!user) return null;

  const role = text(user.role).toUpperCase();
  if (ADMIN_ROLE_SET.has(role)) return 'admin';
  if (PRO_ROLE_SET.has(role)) return 'role';

  const metadata = parseMetadata(user.metadata);

  const accountType = text(metadata.accountType ?? user.accountType).toLowerCase();
  if (PRO_ACCOUNT_TYPE_SET.has(accountType)) return 'accountType';

  const declared = Array.isArray(metadata.declaredRoles)
    ? metadata.declaredRoles
    : Array.isArray(user.declaredRoles)
      ? user.declaredRoles
      : [];
  if (declared.some((activity) => PRO_ACTIVITY_SET.has(text(activity).toUpperCase()))) return 'declaredRole';

  // companyBadge is only sent for ACTIVE team memberships (GET /auth/session); the
  // backend checks the same membership in the database.
  const badge = asRecord(user.companyBadge);
  if (badge && text(badge.companyId)) return 'team';

  return null;
}

/** True when the session alone shows `user` is a professional account. */
export function isProfessional(user: ProfessionalSignals | null | undefined): boolean {
  return professionalVia(user) !== null;
}

const ADVERTISER_ROLES: readonly string[] = [...PRO_ROLES, ...ADMIN_ROLES];

/**
 * Whether a signed-in `user` may open the subscription plans page (/membership).
 *
 * Plans are broker and company listing-quota plans, so the page's gate
 * (components/auth/AdvertiserRoute) admits only a broker or company accountType,
 * or a broker, agent, company-admin or staff role, and sends every other signed-in
 * user to /addons. That is narrower than isProfessional(): an appraiser or a
 * property manager known by their activity is professional but cannot subscribe.
 * Links to /membership (the market preview's «اطّلع على الباقات») use this same
 * rule, so they never lead to a redirect. Exact comparisons, as the gate has
 * always made them.
 */
export function canOpenPlansPage(user: ProfessionalSignals | null | undefined): boolean {
  if (!user) return false;
  const { accountType, role } = user;
  return (
    accountType === 'broker' ||
    accountType === 'company' ||
    (typeof role === 'string' && ADVERTISER_ROLES.includes(role))
  );
}
